import React, { useEffect, useMemo, useState } from "react";
import {
  FaSave,
  FaTimes,
  FaFilePdf,
  FaImage,
  FaUpload,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Button from "../../../../components/Button/Button";

import {
  addPassageCeintureDocument,
  updatePassageCeintureDocument,
  uploadPassageCeintureImage,
  uploadPassageCeinturePdf,
  deletePassageCeintureImage,
  deletePassageCeinturePdf,
} from "../../../../services/passageCeintureDocumentsServices";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const quillModules = {
  toolbar: [
    [{ header: [false, 3, 4] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

const normalizeText = (value = "") => value.trim().replace(/\s+/g, " ");

const normalizePlainText = (value = "") => value.trim().replace(/\s+/g, " ");

const normalizeRichText = (value = "") => {
  const html = String(value || "").trim();

  const plain = html
    .replace(/<p><br><\/p>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]*>/g, "")
    .trim();

  return plain ? html : "";
};

const beltLabel = (b) =>
  (b &&
    (b.label ||
      b.nom ||
      b.name ||
      b.belt_name ||
      b.couleur ||
      b.color ||
      b.belt)) ||
  String(b || "");

export default function ProgressionDocumentModal({
  isOpen,
  onClose,
  document,
  belts = [],
  onSaved,
}) {
  const isEdit = !!document?.id;

  const [formData, setFormData] = useState({
    titre: "",
    description: "",
    ceinture: "",
    image_url: "",
    pdf_url: "",
    ordre_affichage: 0,
    is_published: true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      titre: document?.titre || "",
      description: document?.description || "",
      ceinture: document?.ceinture || "",
      image_url: document?.image_url || "",
      pdf_url: document?.pdf_url || "",
      ordre_affichage: document?.ordre_affichage ?? 0,
      is_published:
        document?.is_published === undefined ? true : !!document.is_published,
    });

    setImageFile(null);
    setPdfFile(null);
    setSubmitting(false);
  }, [isOpen, document]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!normalizeText(formData.titre)) {
      toast.error("Le titre est obligatoire.");
      return;
    }

    if (!normalizeText(formData.ceinture)) {
      toast.error("La ceinture est obligatoire.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        titre: normalizePlainText(formData.titre),
        description: normalizeRichText(formData.description) || null,
        ceinture: normalizePlainText(formData.ceinture),
        image_url: normalizePlainText(formData.image_url) || null,
        pdf_url: normalizePlainText(formData.pdf_url) || null,
        ordre_affichage: Number(formData.ordre_affichage) || 0,
        is_published: !!formData.is_published,
      };

      let savedDoc;

      if (isEdit) {
        savedDoc = await updatePassageCeintureDocument(document.id, payload);
      } else {
        savedDoc = await addPassageCeintureDocument(payload);
      }

      const targetId = savedDoc?.id || document?.id;

      if (imageFile && targetId) {
        await uploadPassageCeintureImage(targetId, imageFile);
      }

      if (pdfFile && targetId) {
        await uploadPassageCeinturePdf(targetId, pdfFile);
      }

      toast.success(isEdit ? "Document mis à jour" : "Document créé");
      await onSaved?.();
      onClose?.();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!document?.id) return;

    try {
      await deletePassageCeintureImage(document.id);
      toast.success("Image supprimée");
      await onSaved?.();
      onClose?.();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression de l'image");
    }
  };

  const handleDeletePdf = async () => {
    if (!document?.id) return;

    try {
      await deletePassageCeinturePdf(document.id);
      toast.success("PDF supprimé");
      await onSaved?.();
      onClose?.();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression du PDF");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
        <div className="bg-gradient-to-r from-blue-600 via-violet-600 to-pink-600 px-6 py-5 text-white">
          <h3 className="text-xl font-black uppercase">
            {isEdit ? "Modifier le document" : "Ajouter un document"}
          </h3>
          <p className="mt-1 text-sm text-white/90">
            Image, PDF et paramètres d’affichage par ceinture.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto p-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Titre *
                </label>
                <input
                  type="text"
                  name="titre"
                  value={formData.titre}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none focus:border-violet-500 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Ceinture *
                </label>
                <select
                  name="ceinture"
                  value={formData.ceinture}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none focus:border-violet-500 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                >
                  <option value="">Sélectionner une ceinture</option>
                  {belts.map((b, index) => {
                    const label = (beltLabel(b) || "").trim();
                    if (!label) return null;
                    return (
                      <option key={`${label}-${index}`} value={label}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Description
                </label>

                <div className="rounded-2xl overflow-hidden border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950">
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: value,
                      }))
                    }
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Saisir une description enrichie..."
                    className="bg-white dark:bg-slate-950 text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Ordre d’affichage
                  </label>
                  <input
                    type="number"
                    min="0"
                    name="ordre_affichage"
                    value={formData.ordre_affichage}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none focus:border-violet-500 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div className="flex items-end">
                  <label className="inline-flex items-center gap-3 rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-800 dark:border-slate-700 dark:text-slate-100">
                    <input
                      type="checkbox"
                      name="is_published"
                      checked={!!formData.is_published}
                      onChange={handleChange}
                    />
                    Publié
                  </label>
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div className="rounded-3xl border border-slate-200 p-4 dark:border-slate-800">
                <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                  <FaImage />
                  Image
                </div>

                <input
                  type="text"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  placeholder="URL image externe (optionnel)"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none focus:border-violet-500 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />

                <div className="mt-3">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-slate-700 dark:text-slate-200"
                  />
                </div>

                {isEdit && !!document?.image_url && (
                  <div className="mt-3">
                    <Button
                      type="button"
                      text="Supprimer l’image"
                      icon={FaTrash}
                      onClick={handleDeleteImage}
                      className="!rounded-2xl !bg-red-600 hover:!bg-red-700"
                    />
                  </div>
                )}
              </div>

              <div className="rounded-3xl border border-slate-200 p-4 dark:border-slate-800">
                <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                  <FaFilePdf />
                  PDF
                </div>

                <input
                  type="text"
                  name="pdf_url"
                  value={formData.pdf_url}
                  onChange={handleChange}
                  placeholder="URL PDF externe (optionnel)"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none focus:border-violet-500 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />

                <div className="mt-3">
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-slate-700 dark:text-slate-200"
                  />
                </div>

                {isEdit && !!document?.pdf_url && (
                  <div className="mt-3">
                    <Button
                      type="button"
                      text="Supprimer le PDF"
                      icon={FaTrash}
                      onClick={handleDeletePdf}
                      className="!rounded-2xl !bg-red-600 hover:!bg-red-700"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-3">
            <Button
              type="button"
              text="Fermer"
              icon={FaTimes}
              onClick={onClose}
              className="!rounded-2xl !bg-slate-200 !text-slate-900 hover:!bg-slate-300 dark:!bg-slate-800 dark:!text-white dark:hover:!bg-slate-700"
            />
            <Button
              type="submit"
              text={submitting ? "Enregistrement..." : "Enregistrer"}
              icon={FaSave}
              className="!rounded-2xl"
              disabled={submitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
